import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { useState } from 'react';
import { toast } from 'sonner';
import { useErrorMessages, useUserSession } from '../../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../../models/Logging';
import { getDisplayName } from '../../../../../models/NameUtils';
import { getMultilineFormattedAddress } from '../../../../../models/UiUtils';
import { useUserPreferencesMutation } from '../../../../../queries';
import { Dialog, DialogHeader, DialogPopup, DialogTitle } from '../../../../components-v2/Dialog';
import { HwcSettingsSignatureInputField } from '../../../../containers/Settings/components/HwcSettingsSignatureInput';
import { HwcSettingsWritingStyleInputWithPreview } from '../../../../containers/Settings/components/HwcSettingsWritingStyleInputWithPreview';
import { baseStyleSheet, bs } from '../../../../styles/styles';
import { DeprecatedTextArea } from '../../../DeprecatedTextArea';
import { INameExtendedAddress, UpdateAddress } from '../../../entities/UpdateAddressModal';
import { LoadingSpinner } from '../../../LoadingSpinner';
import { styleSheet } from './styles';

interface IProps {
	forUser?: Api.IUser;
	onSave?: (result?: Api.IUserPreferences) => void;
	onCancel?: () => void;
}

export const handwritingStyleFontMap: Record<Api.HandwritingStyle, { fontFamily: string; fontSize: number }> = {
	[Api.HandwritingStyle.Aladdin]: { fontFamily: Api.HandwritingStyle.Aladdin, fontSize: 21 },
	[Api.HandwritingStyle.Cinderella]: { fontFamily: Api.HandwritingStyle.Cinderella, fontSize: 32 },
	[Api.HandwritingStyle.Donald]: { fontFamily: Api.HandwritingStyle.Donald, fontSize: 21 },
	[Api.HandwritingStyle.Nemo]: { fontFamily: Api.HandwritingStyle.Nemo, fontSize: 30 },
	[Api.HandwritingStyle.Scar]: { fontFamily: Api.HandwritingStyle.Scar, fontSize: 30 },
	[Api.HandwritingStyle.Tarzan]: { fontFamily: Api.HandwritingStyle.Tarzan, fontSize: 30 },
	[Api.HandwritingStyle.Woody]: { fontFamily: Api.HandwritingStyle.Woody, fontSize: 30 },
};

function getReturnAddress(returnContact: Api.IHandwrittenCardReturnContact) {
	const displayName = getDisplayName({ firstName: returnContact.firstName, lastName: returnContact.lastName });
	return `${displayName ? `${displayName}\n` : ''}${getMultilineFormattedAddress(returnContact.address ?? {})}`.trim();
}

export function HandwrittenCardSettings({ forUser = null, onSave, onCancel }: IProps) {
	const userSession = useUserSession();
	const selectedUser = forUser ?? userSession.user;
	const errorMessages = useErrorMessages();
	const { logApiError } = useEventLogging('HandwrittenCardSettings');
	const mutation = useUserPreferencesMutation({
		onError: error => {
			errorMessages.pushApiError(error);
			logApiError('MutateHandwrittenCardPrefs-Error', error);
		},
		onSuccess: data => {
			if (!forUser) {
				userSession.updateUser({
					...selectedUser,
					userPreferences: data,
				});
			}

			toast.success('Changes applied successfully');
		},
	});
	const [handwritingStyle, setHandwritingStyle] = useState<Api.HandwritingStyle>(
		selectedUser.userPreferences?.handwrittenCardPreferences?.handwritingStyle ?? Api.HandwritingStyle.Aladdin
	);
	const [signature, setSignature] = useState<string>(
		selectedUser.userPreferences?.handwrittenCardPreferences?.signature ?? ''
	);
	const [returnContact, setReturnContact] = useState<Api.IHandwrittenCardReturnContact>(
		selectedUser.userPreferences?.handwrittenCardPreferences?.returnContact ?? {}
	);
	const [isUpdateAddressModalOpen, setIsUpdateAddressModalOpen] = useState(false);
	const returnAddressStr = getReturnAddress(returnContact);
	const handleSave = () => {
		const preferences: Api.IUserPreferences = {
			...selectedUser.userPreferences,
			handwrittenCardPreferences: {
				...selectedUser.userPreferences?.handwrittenCardPreferences,
				handwritingStyle,
				returnContact: Object.keys(returnContact).length > 0 ? returnContact : undefined,
				signature,
			},
		};
		if (forUser) {
			onSave(preferences);
			return;
		}
		mutation.mutate({
			preferences,
		});
	};
	const onCancelChange = () => {
		setHandwritingStyle(
			selectedUser.userPreferences?.handwrittenCardPreferences?.handwritingStyle ?? Api.HandwritingStyle.Aladdin
		);
		setReturnContact(selectedUser.userPreferences?.handwrittenCardPreferences?.returnContact ?? {});
		setSignature(selectedUser.userPreferences?.handwrittenCardPreferences?.signature ?? '');
		onCancel();
	};
	const handleSaveAddress = (_address: INameExtendedAddress) => {
		const { firstName, lastName, ...address } = _address;
		setReturnContact({ address, firstName, lastName });
		setIsUpdateAddressModalOpen(false);
	};
	return (
		<div className={css(styleSheet.settingContentRoot)}>
			<HwcSettingsWritingStyleInputWithPreview
				handwritingStyle={handwritingStyle}
				onChangeHandWritingStyle={setHandwritingStyle}
			/>
			<HwcSettingsSignatureInputField
				inputProps={{
					value: signature,
					onChange: ev => setSignature(ev.target.value),
					disabled: mutation.isPending,
					style: {
						fontFamily: handwritingStyleFontMap[handwritingStyle].fontFamily,
						fontSize: `${handwritingStyleFontMap[handwritingStyle].fontSize}px`,
					},
				}}
			/>
			<div className={css(styleSheet.formRow)}>
				<label className={css(styleSheet.formLabel)}>Return address</label>
				<div className={css(bs.w80, bs.flex)}>
					<DeprecatedTextArea
						inputClassName={css(styleSheet.formReturnAddressInput)}
						value={returnAddressStr}
						inputId='hwc-return-address'
						disabled={true}
						className={css(bs.flex1)}
					/>
					<button
						className={css(baseStyleSheet.brandLink, styleSheet.formEditReturnAddressBtn)}
						onClick={() => setIsUpdateAddressModalOpen(true)}
					>
						Edit
					</button>
				</div>
			</div>
			{mutation.isPending ? <LoadingSpinner className={css(styleSheet.loadingSpinner)} type='large' /> : null}
			<div className={css(bs.flex, bs.gap2, bs.mt5)}>
				<button className={css(baseStyleSheet.ctaButton)} onClick={handleSave} disabled={mutation.isPending}>
					Save
				</button>
				<button className={css(baseStyleSheet.ctaButtonReverse)} onClick={onCancelChange} disabled={mutation.isPending}>
					Cancel
				</button>
			</div>
			<Dialog open={isUpdateAddressModalOpen} onOpenChange={setIsUpdateAddressModalOpen}>
				<DialogPopup>
					<DialogHeader>
						<DialogTitle>Add Address</DialogTitle>
					</DialogHeader>
					<UpdateAddress
						withNames
						initAddress={{
							firstName: returnContact.firstName,
							lastName: returnContact.lastName,
							...returnContact.address,
						}}
						onClickToSave={handleSaveAddress}
						onCancel={() => setIsUpdateAddressModalOpen(false)}
					/>
				</DialogPopup>
			</Dialog>
		</div>
	);
}
